import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { uploadSmart } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { typeLabel, FileChips, projectInfo, UploadProgress, dropProps, parsePrice } from "@/components/shared";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CalendarIcon, Check, ChevronsUpDown, Paperclip, X, FileText, Image as ImageIcon, Send } from "lucide-react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";

const DEFAULT_MESSAGE = `Sayın Yetkili,

Cop31 Antalya projesi için GettProposal üzerinden, aşağıda detayları ve ekleri paylaşılan iş kalemi için firmanızdan fiyat teklifi talep ediyoruz.

Teklifinizi hazırlarken birim fiyatları, teslim süresini ve teklifinizin geçerlilik süresini belirtmenizi önemle rica ederiz. Teklifinizi belirtilen son tarihe kadar portal üzerinden iletebilirsiniz.

Teklifinize Led Ekranlar, televizyonlar, ses sistemleri başta olmak üzere teknik malzemelerin dahil edilmemesini ve teklifinizin açıklama kısmında tüm detayları belirtmenizi rica ederiz.

Göstereceğiniz ilgi ve iş birliği için şimdiden teşekkür ederiz.

Saygılarımızla`;

const buildReopenMessage = (tpl, projectName) => {
  const opening = `${projectName} projesi yeterli sayıda başvuru olmaması ve/veya istenilen fiyat aralığına ulaşılamaması nedeni ile tekrar teklif toplama sürecine açılmıştır.`;
  const paras = tpl.split(/\n\s*\n/);
  if (paras.length >= 2) {
    paras[1] = opening;
    return paras.join("\n\n");
  }
  return `Sayın Yetkili,\n\n${opening}\n\nTeklifinizi hazırlarken birim fiyatları, teslim süresini ve teklifinizin geçerlilik süresini belirtmenizi önemle rica ederiz. Teklifinizi belirtilen son tarihe kadar portal üzerinden iletebilirsiniz.\n\nGöstereceğiniz ilgi ve iş birliği için şimdiden teşekkür ederiz.\n\nSaygılarımızla`;
};

export default function QuoteRequestPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [comboOpen, setComboOpen] = useState(false);
  const [projectComboOpen, setProjectComboOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [currency, setCurrency] = useState("free");
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [templateText, setTemplateText] = useState(DEFAULT_MESSAGE);
  const autoMsgRef = useRef(null);
  const [deadline, setDeadline] = useState(null);
  const [deadlineTime, setDeadlineTime] = useState("18:00");
  const trNow = new Date(new Date().toLocaleString("en-US", { timeZone: "Europe/Istanbul" }));
  const deadlineIsToday = deadline && format(deadline, "yyyy-MM-dd") === format(trNow, "yyyy-MM-dd");
  const deadlineTimeOpts = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, "0")}:00`)
    .filter((t) => !deadlineIsToday || parseInt(t, 10) > trNow.getHours());
  useEffect(() => {
    if (deadlineTimeOpts.length && !deadlineTimeOpts.includes(deadlineTime)) setDeadlineTime(deadlineTimeOpts[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadline]);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [requestedIds, setRequestedIds] = useState([]);
  const [requestedAt, setRequestedAt] = useState({});
  const [serviceItems, setServiceItems] = useState([]);
  const [selInc, setSelInc] = useState([]);
  const [selExc, setSelExc] = useState([]);
  const [targetPrice, setTargetPrice] = useState("");

  useEffect(() => {
    setErrors((e) => {
      if (Object.keys(e).length === 0) return e;
      const n = { ...e };
      if (selected.length > 0) delete n.suppliers;
      if (projectId) delete n.project;
      if (message.trim()) delete n.message;
      if (deadline) delete n.deadline;
      if (!targetPrice.trim() || (parsePrice(targetPrice) > 0 && currency !== "free")) delete n.target;
      return n;
    });
  }, [selected, projectId, message, deadline, targetPrice, currency]);

  useEffect(() => {
    api.get("/service-items").then((r) => setServiceItems(r.data)).catch(() => {});
    api.get("/settings/request-template").then((r) => { setTemplateText(r.data.text); setMessage((cur) => (cur === DEFAULT_MESSAGE && !autoMsgRef.current ? r.data.text : cur)); }).catch(() => {});
  }, []);
  const fileInput = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCustomer = user?.role === "customer";

  useEffect(() => {
    api.get(isCustomer ? "/customer/suppliers" : "/users").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get(isCustomer ? "/customer/projects" : "/projects").then((r) => setProjects([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr")))).catch(() => {});
  }, [isCustomer]);

  const [sortMode, setSortMode] = useState("name");
  const sorted = useMemo(() => {
    const arr = [...suppliers].filter((s) => s.is_active !== false);
    if (sortMode === "won") {
      arr.sort((a, b) => (b.won_count || 0) - (a.won_count || 0) || (b.offer_count || 0) - (a.offer_count || 0) || a.company_name.localeCompare(b.company_name, "tr"));
    } else {
      arr.sort((a, b) => a.company_name.localeCompare(b.company_name, "tr"));
    }
    return arr;
  }, [suppliers, sortMode]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const selectProject = (v) => {
    if (!v) return;
    setProjectId(v);
    setErrors((e) => ({ ...e, project: undefined }));
    api
      .get(`/projects/${v}/requested-suppliers`)
      .then((r) => {
        const ids = r.data.supplier_ids || [];
        setRequestedIds(ids);
        setRequestedAt(r.data.requested_at || {});
        setSelected((s) => s.filter((id) => !ids.includes(id)));
        const last = r.data.last_request;
        if (last) {
          let applied = false;
          if (last.currency) { setCurrency(last.currency); applied = true; }
          if (last.deadline) {
            const d = new Date(last.deadline + "T00:00:00");
            if (d >= new Date(new Date().toDateString())) {
              setDeadline(d);
              if (last.deadline_time) setDeadlineTime(last.deadline_time);
              setErrors((e) => ({ ...e, deadline: undefined }));
              applied = true;
            }
          }
          if (applied) toast.info("Bu projenin önceki talebindeki para birimi ve son tarih otomatik seçildi");
        }
      })
      .catch(() => { setRequestedIds([]); setRequestedAt({}); });
    const proj = projects.find((x) => x.id === v);
    setFiles((f) => {
      const manual = f.filter((x) => !x.fromProject);
      const projFiles = (proj?.files || []).filter((pf) => !manual.some((m) => m.id === pf.id)).map((pf) => ({ ...pf, fromProject: true }));
      return [...projFiles, ...manual];
    });
  };

  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (!projectId) return;
    const proj = projects.find((x) => x.id === projectId);
    if (!proj) return;
    const isReopen = !proj.open_tender && ((proj.request_count || 0) > 0 || proj.has_request);
    setMessage((cur) => {
      const untouched = cur === templateText || cur === DEFAULT_MESSAGE || cur === autoMsgRef.current;
      if (!untouched) return cur;
      const next = isReopen ? buildReopenMessage(templateText, proj.name) : templateText;
      if (next !== cur) {
        autoMsgRef.current = next;
        if (isReopen) toast.info("Yeniden ihale: talep metni otomatik güncellendi");
      }
      return next;
    });
  }, [projectId, templateText, projects]);

  const preselected = useRef(false);
  useEffect(() => {
    const pid = searchParams.get("proje");
    if (!preselected.current && pid && projects.some((p) => p.id === pid)) {
      preselected.current = true;
      const p = projects.find((x) => x.id === pid);
      if (p.open_tender) {
        toast.error("Bu proje için zaten bir ihale açılmış. Aynı proje için ikinci ihale açılamaz.");
        return;
      }
      selectProject(pid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  const onFiles = async (e) => {
    const list = Array.from(e?.target?.files || e || []);
    if (list.length === 0) return;
    setUploading(true);
    try {
      const data = await uploadSmart(list, setProgress);
      setFiles((f) => [...f, ...data]);
    } catch (err) {
      toast.error(err?.message || apiError(err));
    } finally {
      setUploading(false);
      setProgress(null);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const submit = async () => {
    const e = {};
    if (selected.length === 0) e.suppliers = "En az bir kullanıcı seçin";
    if (!projectId) e.project = "Proje seçin";
    if (!message.trim()) e.message = "Talep metni zorunludur";
    if (!deadline) e.deadline = "Son tarih seçin";
    const tp = targetPrice.trim() ? parsePrice(targetPrice) : null;
    const useTarget = selected.length === 1 && targetPrice.trim();
    if (useTarget) {
      if (!tp || tp <= 0) e.target = "Geçerli bir hedef fiyat girin";
      else if (currency === "free") e.target = "Hedef fiyat için para birimi seçin";
    }
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setBusy(true);
    try {
      await api.post("/requests", {
        project_id: projectId,
        currency: currency === "free" ? null : currency,
        message,
        deadline: format(deadline, "yyyy-MM-dd"),
        deadline_time: deadlineTime,
        supplier_ids: selected,
        file_ids: files.map((f) => f.id),
        included_services: selInc,
        excluded_services: selExc,
        ...(useTarget ? { target_price: tp } : {}),
      });
      toast.success(useTarget
        ? "Hedef fiyat önerisi gönderildi — tedarikçi e-postadaki bağlantıdan kabul/red/karşı teklif verebilir"
        : "Teklif talebi gönderildi ve tedarikçiler e-posta ile bilgilendirildi");
      navigate(isCustomer ? "/musteri/teklifler" : "/admin/teklifler");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8 fade-in max-w-3xl">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Fiyat Talebi</div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tighter mt-1">Teklif Al</h1>
      </div>

      <div className="space-y-6">
        <div className="space-y-1.5">
          <Label>Proje Seç</Label>
          <Popover open={projectComboOpen} onOpenChange={setProjectComboOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" data-testid="project-select-trigger" className="w-full justify-between rounded-none bg-white border-slate-200 h-11 font-normal">
                <span className="truncate text-left">
                  {(() => { const p = projects.find((x) => x.id === projectId); return p ? `${p.name} — ${projectInfo(p)}` : "Proje seçin"; })()}
                </span>
                <ChevronsUpDown size={14} className="opacity-50 shrink-0" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-white border-slate-200 rounded-none">
              <Command className="bg-white">
                <CommandInput placeholder="Proje adı, stand no veya bölge ara..." data-testid="project-search-input" />
                <CommandList>
                  <CommandEmpty>Proje bulunamadı.</CommandEmpty>
                  <CommandGroup>
                    {projects.map((p) => {
                      const hasTender = !!p.open_tender;
                      const reopenable = !hasTender && ((p.request_count || 0) > 0 || p.has_request);
                      return (
                        <CommandItem
                          key={p.id}
                          value={`${p.name} ${p.stand_no || ""} ${p.zone || ""} ${p.project_type || ""}`}
                          disabled={hasTender}
                          onSelect={() => {
                            if (hasTender) return;
                            selectProject(p.id);
                            setProjectComboOpen(false);
                          }}
                          data-testid={`project-option-${p.id}`}
                          className={hasTender ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
                        >
                          <Check size={14} className={`mr-2 ${projectId === p.id ? "opacity-100" : "opacity-0"}`} />
                          <span className="truncate">{p.name} — {projectInfo(p)}</span>
                          {hasTender && (
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => { e.stopPropagation(); if (!isCustomer && p.request_id) navigate(`/admin/teklifler/${p.request_id}`); }}
                              onKeyDown={(e) => { if (e.key === "Enter" && !isCustomer && p.request_id) navigate(`/admin/teklifler/${p.request_id}`); }}
                              title={isCustomer ? "Bu proje için ihale açık" : "İhaleye gitmek için tıklayın"}
                              style={{ pointerEvents: "auto" }}
                              data-testid={`project-tender-open-${p.id}`}
                              className={`ml-auto shrink-0 text-[10px] uppercase tracking-wider font-bold text-red-500 border border-red-200 bg-red-50 px-1.5 py-0.5 ${!isCustomer ? "cursor-pointer hover:bg-red-100 transition-colors duration-200" : ""}`}
                            >
                              İhale Açıldı{!isCustomer ? " ↗" : ""}
                            </span>
                          )}
                          {reopenable && (
                            <span className="ml-auto shrink-0 text-[10px] uppercase tracking-wider font-bold text-amber-600 border border-amber-200 bg-amber-50 px-1.5 py-0.5" data-testid={`project-reopenable-${p.id}`}>
                              Teklifsiz Kapandı — Yeniden Açılabilir
                            </span>
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {projects.length === 0 && <p className="text-xs text-slate-500">Henüz proje yok. Önce "Proje Oluştur" menüsünden proje ekleyin.</p>}
          {errors.project && <p className="text-red-500 text-sm" data-testid="error-project">{errors.project}</p>}
        </div>

        <div className="space-y-1.5">
          <Label>Tedarikçi Seç</Label>
          <Popover open={comboOpen} onOpenChange={setComboOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" data-testid="supplier-combobox-trigger" className="w-full justify-between rounded-none bg-white border-slate-200 hover:bg-slate-100 h-11 font-normal">
                {selected.length === 0 ? <span className="text-slate-500">Tedarikçi seçin (birden fazla seçilebilir)...</span> : `${selected.length} tedarikçi seçildi`}
                <ChevronsUpDown size={15} className="text-slate-500" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0 bg-white border-slate-200 rounded-none">
              <Command className="bg-white">
                <CommandInput placeholder="Firma ara..." data-testid="supplier-search-input" />
                <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-slate-100">
                  <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">Sırala:</span>
                  <button
                    type="button"
                    onClick={() => setSortMode("name")}
                    data-testid="sort-by-name"
                    className={`text-[11px] px-2 py-0.5 border transition-colors duration-150 ${sortMode === "name" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}
                  >
                    A-Z
                  </button>
                  <button
                    type="button"
                    onClick={() => setSortMode("won")}
                    data-testid="sort-by-won"
                    className={`text-[11px] px-2 py-0.5 border transition-colors duration-150 ${sortMode === "won" ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}
                  >
                    En Çok Kazanan
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const selectable = sorted.filter((s) => !(s.company_name && requestedIds.includes(s.id))).map((s) => s.id);
                      setSelected((prev) => Array.from(new Set([...prev, ...selectable])));
                    }}
                    data-testid="select-all-suppliers"
                    className="ml-auto text-[11px] px-2 py-0.5 border border-slate-200 text-slate-600 hover:border-slate-900 hover:text-slate-900 transition-colors duration-150"
                  >
                    Tümünü Seç
                  </button>
                  {selected.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelected([])}
                      data-testid="clear-all-suppliers"
                      className="text-[11px] px-2 py-0.5 border border-slate-200 text-slate-600 hover:border-red-400 hover:text-red-500 transition-colors duration-150"
                    >
                      Temizle
                    </button>
                  )}
                </div>
                <CommandList>
                  <CommandEmpty>Sonuç bulunamadı.</CommandEmpty>
                  <CommandGroup>
                    {sorted.map((s) => {
                      const isRequested = s.company_name && requestedIds.includes(s.id);
                      const reqDate = isRequested && requestedAt[s.id] ? new Date(requestedAt[s.id]).toLocaleDateString("tr-TR") : null;
                      return (
                        <CommandItem
                          key={s.id}
                          value={s.company_name}
                          onSelect={() => { if (!isRequested) toggle(s.id); }}
                          title={isRequested ? `Bu tedarikçiden ${reqDate ? reqDate + " tarihinde" : "daha önce"} teklif talep edildi` : undefined}
                          data-testid={`supplier-option-${s.username || s.id}`}
                          className={isRequested ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
                        >
                          <div className={`mr-2 flex h-4 w-4 items-center justify-center border ${selected.includes(s.id) ? "bg-white border-white text-black" : "border-slate-400"}`}>
                            {selected.includes(s.id) && <Check size={11} />}
                          </div>
                          <span className={isRequested ? "text-slate-400" : ""}>{s.company_name}</span>
                          {(s.offer_count || 0) > 0 && (
                            <span className="ml-2 text-[11px] whitespace-nowrap" data-testid={`supplier-stats-${s.username || s.id}`}>
                              <span className="text-slate-500">{s.offer_count} teklif</span>
                              {(s.won_count || 0) > 0 && <span className="text-emerald-600 font-semibold"> · {s.won_count} kazandı</span>}
                            </span>
                          )}
                          {isRequested ? (
                            <span className="ml-2 text-xs text-slate-400" data-testid={`already-requested-${s.username || s.id}`}>
                              Teklif Talep Edildi{reqDate ? ` · ${reqDate}` : ""}
                            </span>
                          ) : (
                            <span className="ml-2 text-xs text-slate-500">{typeLabel(s.account_type)}</span>
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              {selected.map((id) => {
                const s = suppliers.find((x) => x.id === id);
                return s ? (
                  <Badge key={id} variant="outline" className="rounded-none border-slate-300 text-slate-700 gap-1.5 py-1" data-testid={`selected-supplier-${s.username || s.id}`}>
                    {s.company_name}
                    <button onClick={() => toggle(id)} className="hover:text-red-600 transition-colors duration-200" data-testid={`remove-supplier-${s.username || s.id}`}>
                      <X size={12} />
                    </button>
                  </Badge>
                ) : null;
              })}
            </div>
          )}
          {errors.suppliers && <p className="text-red-500 text-sm" data-testid="error-suppliers">{errors.suppliers}</p>}
        </div>

        {!isCustomer && selected.length === 1 && (
          <div className="border border-amber-200 bg-amber-50/40 p-4 space-y-1.5" data-testid="target-price-section">
            <Label className="flex items-center gap-1.5 text-amber-800">Hedef Fiyat (opsiyonel)</Label>
            <Input
              type="text"
              inputMode="decimal"
              value={targetPrice}
              onChange={(e) => setTargetPrice(e.target.value)}
              placeholder="Örn: 1.500.000"
              data-testid="target-price-input"
              className="rounded-none bg-white border-amber-300 h-11"
            />
            {targetPrice.trim() && !isNaN(parsePrice(targetPrice)) && parsePrice(targetPrice) > 0 && (
              <p className="text-xs text-emerald-700 font-medium" data-testid="target-price-preview">
                = {parsePrice(targetPrice).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {currency === "free" ? "(para birimi seçin)" : currency}
              </p>
            )}
            <p className="text-xs text-slate-600">
              Tek tedarikçi seçtiniz. Hedef fiyat girerseniz tedarikçiye "Bu fiyata yapar mısınız?" e-postası gönderilir;
              bağlantı üzerinden <strong>kabul edebilir, reddedebilir veya karşı teklif verebilir</strong>. Karşı tekliflere siz de
              karşılaştırma sayfasından yeni öneriyle yanıt verebilirsiniz. Hedef fiyat için para birimi seçilmelidir.
            </p>
            {errors.target && <p className="text-red-500 text-sm" data-testid="error-target">{errors.target}</p>}
          </div>
        )}

        <div className="space-y-1.5">
          <Label>Dosya Ekle</Label>
          <input ref={fileInput} type="file" multiple className="hidden" onChange={onFiles} data-testid="file-input" />
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            {...dropProps(onFiles, setDragOver)}
            data-testid="file-dropzone"
            className={`w-full border border-dashed transition-colors duration-200 p-8 flex flex-col items-center gap-2 ${dragOver ? "border-slate-900 bg-slate-100 text-slate-900" : "border-slate-300 bg-white hover:border-slate-400 text-slate-500"}`}
          >
            <Paperclip size={20} />
            <span className="text-sm">{uploading ? "Yükleniyor..." : dragOver ? "Dosyaları buraya bırakın" : "Dosya seçmek için tıklayın veya sürükleyip bırakın"}</span>
          </button>
          {uploading && <UploadProgress progress={progress} />}
          {files.length > 0 && (
            <div className="pt-2 space-y-1.5">
              <FileChips files={files} onRemove={(id) => setFiles((arr) => arr.filter((x) => x.id !== id))} />
              {files.some((f) => f.fromProject) && (
                <p className="text-xs text-slate-500" data-testid="project-files-note">Proje dosyaları otomatik eklendi. Önizlemek için dosyaya tıklayın.</p>
              )}
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <Label>Para Birimi</Label>
          <Select value={currency} onValueChange={(v) => { if (v) setCurrency(v); }}>
            <SelectTrigger className="rounded-none bg-white border-slate-200 h-11" data-testid="request-currency-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-slate-200 rounded-none">
              <SelectItem value="free" data-testid="currency-option-free">Tedarikçi seçsin (serbest)</SelectItem>
              <SelectItem value="TRY" data-testid="currency-option-TRY">TRY — Türk Lirası</SelectItem>
              <SelectItem value="USD" data-testid="currency-option-USD">USD — Amerikan Doları</SelectItem>
              <SelectItem value="EUR" data-testid="currency-option-EUR">EUR — Euro</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500">
            {currency === "free"
              ? "Her tedarikçi teklif verirken para birimini kendisi seçer."
              : `Tüm tedarikçiler teklifini ${currency} üzerinden verir.`}
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="message">Talep Metni</Label>
          <Textarea id="message" data-testid="request-message-input" value={message} onChange={(e) => setMessage(e.target.value)} rows={10} className="rounded-none bg-white border-slate-200 text-sm leading-relaxed" />
          {errors.message && <p className="text-red-500 text-sm" data-testid="error-message">{errors.message}</p>}
        </div>

        {serviceItems.length > 0 && (
          <div className="space-y-1.5">
            <Label>Dahil / Hariç Hizmetler</Label>
            <p className="text-xs text-slate-500 -mt-0.5">İşaretlediğiniz hizmetler talepte ve tedarikçiye giden e-postada gösterilir. Liste, Ayarlar sayfasından yönetilir.</p>
            <div className="grid sm:grid-cols-2 gap-4 pt-1">
              <div className="border border-emerald-200 bg-emerald-50/30 p-4 space-y-2.5" data-testid="included-services-picker">
                <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">Dahil Hizmetler</div>
                {serviceItems.filter((s) => s.kind === "dahil").map((s) => (
                  <label key={s.id} className="flex items-start gap-2.5 text-sm cursor-pointer">
                    <Checkbox
                      checked={selInc.includes(s.text)}
                      onCheckedChange={(v) => setSelInc((p) => (v ? [...p, s.text] : p.filter((x) => x !== s.text)))}
                      data-testid={`inc-service-${s.id}`}
                      className="mt-0.5 border-emerald-400 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                    />
                    <span>{s.text}</span>
                  </label>
                ))}
                {serviceItems.filter((s) => s.kind === "dahil").length === 0 && <div className="text-xs text-slate-400">Tanımlı hizmet yok.</div>}
              </div>
              <div className="border border-red-200 bg-red-50/30 p-4 space-y-2.5" data-testid="excluded-services-picker">
                <div className="text-xs uppercase tracking-wider text-red-600 font-semibold">Hariç Hizmetler</div>
                {serviceItems.filter((s) => s.kind === "haric").map((s) => (
                  <label key={s.id} className="flex items-start gap-2.5 text-sm cursor-pointer">
                    <Checkbox
                      checked={selExc.includes(s.text)}
                      onCheckedChange={(v) => setSelExc((p) => (v ? [...p, s.text] : p.filter((x) => x !== s.text)))}
                      data-testid={`exc-service-${s.id}`}
                      className="mt-0.5 border-red-400 data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
                    />
                    <span>{s.text}</span>
                  </label>
                ))}
                {serviceItems.filter((s) => s.kind === "haric").length === 0 && <div className="text-xs text-slate-400">Tanımlı hizmet yok.</div>}
              </div>
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <Label>Son Teklif Tarihi (Deadline)</Label>
          <div className="flex flex-col sm:flex-row gap-3">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="deadline-picker-trigger" className="flex-1 justify-start rounded-none bg-white border-slate-200 hover:bg-slate-100 h-11 font-normal gap-2">
                  <CalendarIcon size={15} className="text-slate-500" />
                  {deadline ? format(deadline, "dd MMMM yyyy", { locale: tr }) : <span className="text-slate-500">Tarih seçin</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 bg-white border-slate-200 rounded-none w-auto">
                <Calendar mode="single" selected={deadline} onSelect={setDeadline} locale={tr} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} />
              </PopoverContent>
            </Popover>
            <Select value={deadlineTime} onValueChange={setDeadlineTime}>
              <SelectTrigger className="rounded-none bg-white border-slate-200 h-11 sm:w-36" data-testid="deadline-time-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200 rounded-none max-h-64">
                {deadlineTimeOpts.map((t) => (
                  <SelectItem key={t} value={t} data-testid={`deadline-time-${t.slice(0, 2)}`}>{t}</SelectItem>
                ))}
                {deadlineTimeOpts.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">Bugün için uygun saat kalmadı — ileri bir tarih seçin</div>}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-slate-500">Teklifler seçilen gün ve saate kadar (Türkiye saati) verilebilir.</p>
          {errors.deadline && <p className="text-red-500 text-sm" data-testid="error-deadline">{errors.deadline}</p>}
        </div>

        <Button onClick={submit} disabled={busy || uploading} data-testid="submit-request-button" className="rounded-none h-12 px-8 bg-slate-900 text-white hover:bg-slate-700 font-semibold gap-2 transition-colors duration-200">
          <Send size={15} /> {busy ? "Gönderiliyor..." : "Talep Et"}
        </Button>
      </div>
    </div>
  );
}
